import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';

interface AreaChartProps {
  data: { timestamp: string; count: number }[];
  height?: number;
  timezone?: string;
}

function formatTimestamp(isoStr: string, timezone: string): string {
  try {
    const date = new Date(isoStr);
    return date.toLocaleString(undefined, {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return isoStr;
  }
}

export function AreaChart({ data, height = 300, timezone = 'UTC' }: AreaChartProps) {
  const colors = useChartColors();

  const formattedData = data.map((d) => ({
    ...d,
    time: formatTimestamp(d.timestamp, timezone),
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsAreaChart data={formattedData}>
        <defs>
          <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors.primary} stopOpacity={0.25} />
            <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
        <XAxis
          dataKey="time"
          stroke={colors.axis}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={colors.axis}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => value.toLocaleString()}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: colors.tooltipBg,
            border: `1px solid ${colors.tooltipBorder}`,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            color: colors.text,
          }}
          formatter={(value: number) => [value.toLocaleString(), 'Viewers']}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke={colors.primary}
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorCount)"
          animationDuration={750}
        />
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
}
